import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

/** ---------- Types ---------- */
type VoterHit = {
  campaign_voter_id: string;
  voter_id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  suffix: string | null;
  address1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
};

type ContactMethod = {
  id: string;
  key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
};
type ContactOutcome = {
  id: string;
  key: string;
  label: string;
  sort_order: number;
  is_active: boolean;
};
type RelType = {
  id: string;
  label: string;
  key: string;
  category: string;
  sort_order: number;
  is_active: boolean;
};

/** ---------- UI constants ---------- */
const BRAND = "#0B2E5B";
const PANEL = { bg: "#fff", radius: 14, border: "1px solid #e5e7eb" };
const BTN: React.CSSProperties = {
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #e5e7eb",
  background: "#fff",
  fontWeight: 600,
  cursor: "pointer",
};
const PRIMARY: React.CSSProperties = {
  ...BTN,
  background: BRAND,
  color: "#fff",
  border: "none",
};
const CHIP_BASE: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 999,
  border: "1px solid #e5e7eb",
  background: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  textAlign: "center",
  transition:
    "background 120ms ease, color 120ms ease, border-color 120ms ease, opacity 120ms ease",
};
const CHIP_ACTIVE: React.CSSProperties = {
  background: BRAND,
  color: "#fff",
  border: `2px solid ${BRAND}`,
};
const CHIP_DISABLED: React.CSSProperties = {
  opacity: 0.5,
  cursor: "not-allowed",
  pointerEvents: "none",
};

const SENTIMENT: Array<{ label: string; value: string; color: string }> = [
  { label: "Strong", value: "Strong", color: "#22c55e" },
  { label: "Lean", value: "Lean", color: "#86efac" },
  { label: "Undecided", value: "Undecided", color: "#facc15" },
  { label: "Lean Against", value: "Lean Against", color: "#f97316" },
  { label: "Strong Against", value: "Strong Against", color: "#ef4444" },
];

/** ---------- Helpers ---------- */
function fullName(v: VoterHit | null) {
  if (!v) return "";
  return [v.first_name, v.middle_name, v.last_name, v.suffix]
    .filter(Boolean)
    .join(" ")
    .trim();
}

/** ---------- Page ---------- */
export default function QuickLog() {
  // Global campaign selection from top bar
  const campaignId = useCampaignId();

  // refs for focusing
  const searchRef = useRef<HTMLInputElement | null>(null);
  const firstControlRef = useRef<HTMLButtonElement | null>(null);

  // voter search & selection
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<VoterHit[]>([]);
  const [selected, setSelected] = useState<VoterHit | null>(null);
  const [searching, setSearching] = useState(false);

  // mode
  const [mode, setMode] =
    useState<"contact" | "note" | "relationship">("contact");

  // dynamic contact config
  const [cMethods, setCMethods] = useState<ContactMethod[]>([]);
  const [cOutcomes, setCOutcomes] = useState<ContactOutcome[]>([]);

  // Contact fields (store keys)
  const [methodKey, setMethodKey] = useState<string | null>(null);
  const [resultKey, setResultKey] = useState<string | null>(null);
  const [support, setSupport] = useState<string | null>(null);
  const [cNotes, setCNotes] = useState("");

  // Note fields
  const [nText, setNText] = useState("");
  const [nType, setNType] = useState("");

  // Relationship fields
  const [relTypes, setRelTypes] = useState<RelType[]>([]);
  const [relTypeId, setRelTypeId] = useState("");
  const [relNote, setRelNote] = useState("");
  const [reciprocal, setReciprocal] = useState(true);
  const [relSearch, setRelSearch] = useState("");
  const [relHits, setRelHits] = useState<VoterHit[]>([]);
  const [relTarget, setRelTarget] = useState<VoterHit | null>(null);

  // save state + banner
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  /** Load campaign-specific contact methods/outcomes + relationship types */
  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      const [m, o, rt] = await Promise.all([
        supabase
          .from("campaign_contact_methods")
          .select("id,key,label,sort_order,is_active")
          .eq("campaign_id", campaignId)
          .eq("is_active", true)
          .order("sort_order"),
        supabase
          .from("campaign_contact_outcomes")
          .select("id,key,label,sort_order,is_active")
          .eq("campaign_id", campaignId)
          .eq("is_active", true)
          .order("sort_order"),
        supabase
          .from("campaign_relationship_types")
          .select("id,label,key,category,sort_order,is_active")
          .eq("campaign_id", campaignId)
          .eq("is_active", true)
          .order("sort_order"),
      ]);
      if (!m.error) setCMethods((m.data ?? []) as ContactMethod[]);
      if (!o.error) setCOutcomes((o.data ?? []) as ContactOutcome[]);
      if (!rt.error) setRelTypes((rt.data ?? []) as RelType[]);
      // reset when campaign changes
      resetAll(false);
      setSelected(null);
      setQuery("");
      setHits([]);
      // focus search
      setTimeout(() => searchRef.current?.focus(), 0);
    })();
  }, [campaignId]);

  /** Debounced search for voters (name/address) */
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!campaignId || !query.trim()) {
        setHits([]);
        return;
      }
      setSearching(true);
      const term = `%${query.trim()}%`;
      const { data, error } = await supabase
        .from("campaign_voters_with_address")
        .select(
          "campaign_voter_id, voter_id, first_name, middle_name, last_name, suffix, address1, city, state, zip"
        )
        .eq("campaign_id", campaignId)
        .or(
          `first_name.ilike.${term},last_name.ilike.${term},address1.ilike.${term},city.ilike.${term}`
        )
        .limit(20);
      if (!error) setHits((data ?? []) as VoterHit[]);
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [campaignId, query]);

  /** Debounced search for relationship target */
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!campaignId || !relSearch.trim()) {
        setRelHits([]);
        return;
      }
      const term = `%${relSearch.trim()}%`;
      const { data, error } = await supabase
        .from("campaign_voters_with_address")
        .select(
          "campaign_voter_id, voter_id, first_name, middle_name, last_name, suffix, address1, city, state, zip"
        )
        .eq("campaign_id", campaignId)
        .or(
          `first_name.ilike.${term},last_name.ilike.${term},address1.ilike.${term},city.ilike.${term}`
        )
        .limit(20);
      if (!error) setRelHits((data ?? []) as VoterHit[]);
    }, 250);
    return () => clearTimeout(t);
  }, [campaignId, relSearch]);

  /** Derived guards */
  const canSaveContact = useMemo(
    () => Boolean(campaignId && selected && methodKey && resultKey),
    [campaignId, selected, methodKey, resultKey]
  );
  const canSaveNote = useMemo(
    () => Boolean(campaignId && selected && nText.trim()),
    [campaignId, selected, nText]
  );
  const canSaveRel = useMemo(
    () => Boolean(campaignId && selected && relTarget && relTypeId),
    [campaignId, selected, relTarget, relTypeId]
  );

  /** Helpers */
  const remindSelectVoter = () => {
    setMsg(null);
    setErr("Select a voter first.");
    searchRef.current?.focus();
  };

  function resetAll(clearModeSelections = true) {
    // contact
    setMethodKey(clearModeSelections ? null : methodKey);
    setResultKey(clearModeSelections ? null : resultKey);
    setSupport(null);
    setCNotes("");
    // note
    setNText("");
    setNType("");
    // relationship
    setRelTypeId("");
    setRelNote("");
    setRelSearch("");
    setRelTarget(null);
  }

  function startNewVoter() {
    setSelected(null);
    setQuery("");
    setHits([]);
    resetAll(); // clear all mode selections
    setMsg(null);
    setErr(null);
    setTimeout(() => searchRef.current?.focus(), 0);
  }

  function logAnotherSameVoter() {
    // keep selected voter, keep current mode, clear fields
    resetAll(); // clears fields and leaves mode as-is
    setMsg(null);
    setErr(null);
    // focus first control in the active mode (if available)
    setTimeout(() => firstControlRef.current?.focus(), 0);
  }

  /** Persist support to campaign_voters if provided */
  async function persistSupportIfNeeded(
    campaign_id: string,
    campaign_voter_id: string,
    supportLevel: string | null
  ) {
    if (!supportLevel) return;
    await supabase
      .from("campaign_voters")
      .update({ support_level: supportLevel })
      .eq("id", campaign_voter_id)
      .eq("campaign_id", campaign_id);
  }

  async function save() {
    try {
      setSaving(true);
      setMsg(null);
      setErr(null);

      if (!campaignId || !selected) {
        remindSelectVoter();
        return;
      }

      if (mode === "contact") {
        if (!methodKey || !resultKey) return;
        const session = await supabase.auth.getSession();
        const uid = session.data.session?.user?.id ?? null;

        const { error } = await supabase.from("voter_contacts").insert({
          campaign_id: campaignId,
          voter_id: selected.campaign_voter_id,
          contacted_by: uid,
          contact_method: methodKey,
          contact_type: "quicklog",
          result: resultKey,
          notes: cNotes || null,
        });
        if (error) throw error;

        // Also persist support level to campaign_voters so Dashboard updates
        await persistSupportIfNeeded(
          campaignId,
          selected.campaign_voter_id,
          support
        );

        setMsg("Contact saved. What next?");
        resetAll(); // clear inputs so a second log is fresh
      }

      if (mode === "note") {
        const session = await supabase.auth.getSession();
        const uid = session.data.session?.user?.id ?? null;

        const { error } = await supabase.from("voter_notes").insert({
          campaign_id: campaignId,
          voter_id: selected.campaign_voter_id,
          note_type: nType || null,
          note: nText.trim(),
          created_by: uid,
        });
        if (error) throw error;

        setMsg("Note saved. What next?");
        resetAll();
      }

      if (mode === "relationship") {
        if (!relTarget || !relTypeId) return;
        const session = await supabase.auth.getSession();
        const uid = session.data.session?.user?.id ?? null;

        const payload = {
          campaign_id: campaignId,
          from_kind: "voter",
          from_id: selected.campaign_voter_id,
          to_kind: "voter",
          to_id: relTarget.campaign_voter_id,
          rel_type_id: relTypeId,
          notes: relNote || null,
          created_by: uid,
        };

        const { error } = await supabase.from("relationships").insert([payload]);
        if (error) throw error;

        if (reciprocal) {
          await supabase.from("relationships").insert([
            {
              campaign_id: campaignId,
              from_kind: "voter",
              from_id: relTarget.campaign_voter_id,
              to_kind: "voter",
              to_id: selected.campaign_voter_id,
              rel_type_id: relTypeId,
              notes: relNote || null,
              created_by: uid,
            },
          ]);
        }

        setMsg("Relationship saved. What next?");
        resetAll();
      }

      // focus the first control for the active mode (if the user chooses to stay)
      setTimeout(() => firstControlRef.current?.focus(), 0);
    } catch (e: any) {
      setErr(e?.message ?? "Could not save");
    } finally {
      setSaving(false);
    }
  }

  /** ---------- UI ---------- */
  return (
    <div style={{ padding: 16, background: "#f3f7fb", minHeight: "100vh" }}>
      <h1 style={{ marginTop: 4, marginBottom: 6 }}>Quick Log</h1>
      <div style={{ color: "#6b7280", marginBottom: 12 }}>
        Search a voter, then quickly add a <b>Contact</b>, <b>Note</b>, or{" "}
        <b>Relationship</b>.
      </div>

      {!campaignId && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            borderRadius: 12,
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            color: "#92400E",
          }}
        >
          No campaign selected. Use the campaign menu in the top bar to pick
          one.
        </div>
      )}

      {(msg || err) && (
        <div
          style={{
            marginBottom: 10,
            padding: "10px 12px",
            borderRadius: 12,
            background: msg ? "#ECFDF5" : "#FEF2F2",
            border: `1px solid ${msg ? "#A7F3D0" : "#FCA5A5"}`,
            color: msg ? "#065F46" : "#991B1B",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ marginRight: 8 }}>{msg || err}</span>
          {msg && (
            <>
              <button style={{ ...BTN }} onClick={logAnotherSameVoter}>
                Log another for this voter
              </button>
              <button style={{ ...PRIMARY }} onClick={startNewVoter}>
                Select a different voter
              </button>
            </>
          )}
        </div>
      )}

      {/* Voter search */}
      <Section title="Voter">
        <input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name or address…"
          style={{
            width: "100%",
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid #e5e7eb",
            marginBottom: 8,
          }}
          disabled={!campaignId}
        />
        {selected ? (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          >
            <div className="badge">
              {fullName(selected) || "(no name)"} — {selected.address1 ?? ""}{" "}
              {selected.city ? `• ${selected.city}` : ""}{" "}
              {selected.state ? `, ${selected.state}` : ""}{" "}
              {selected.zip ? ` ${selected.zip}` : ""}
            </div>
            <button style={{ ...BTN }} onClick={startNewVoter}>
              New voter
            </button>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            {searching && <div style={{ color: "#6b7280" }}>Searching…</div>}
            {hits.map((h) => (
              <button
                key={h.campaign_voter_id}
                style={{ ...BTN, textAlign: "left" }}
                onClick={() => {
                  setSelected(h);
                  setErr(null);
                  setMsg(null);
                  // focus first control in active mode
                  setTimeout(() => firstControlRef.current?.focus(), 0);
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {fullName(h) || "(no name)"}
                </div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  {h.address1 ?? ""} {h.city ? `• ${h.city}` : ""}{" "}
                  {h.state ? `, ${h.state}` : ""} {h.zip ? ` ${h.zip}` : ""}
                </div>
              </button>
            ))}
            {!searching && query.trim() && hits.length === 0 && (
              <div style={{ color: "#6b7280" }}>No matches.</div>
            )}
            {!query.trim() && (
              <div style={{ color: "#6b7280" }}>Type to search…</div>
            )}
          </div>
        )}
      </Section>

      {/* Mode picker */}
      <Section title="What do you want to add?">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <Chip
            label="Contact"
            active={mode === "contact"}
            disabled={!selected || !campaignId}
            onClick={() =>
              !selected ? remindSelectVoter() : setMode("contact")
            }
            buttonRef={mode === "contact" ? firstControlRef : undefined}
          />
          <Chip
            label="Note"
            active={mode === "note"}
            disabled={!selected || !campaignId}
            onClick={() => (!selected ? remindSelectVoter() : setMode("note"))}
            buttonRef={mode === "note" ? firstControlRef : undefined}
          />
          <Chip
            label="Relationship"
            active={mode === "relationship"}
            disabled={!selected || !campaignId}
            onClick={() =>
              !selected ? remindSelectVoter() : setMode("relationship")
            }
            buttonRef={mode === "relationship" ? firstControlRef : undefined}
          />
        </div>
      </Section>

      {/* Contact form */}
      {mode === "contact" && (
        <>
          <Section title="Contact method">
            <ChipRowDynamic
              items={cMethods.map((m) => ({ key: m.key, label: m.label }))}
              value={methodKey}
              onChange={(v) =>
                !selected ? remindSelectVoter() : setMethodKey(v)
              }
              disabled={!selected || !campaignId}
              firstRef={firstControlRef}
            />
          </Section>

          <Section title="Result">
            <ChipRowDynamic
              items={cOutcomes.map((o) => ({ key: o.key, label: o.label }))}
              value={resultKey}
              onChange={(v) =>
                !selected ? remindSelectVoter() : setResultKey(v)
              }
              disabled={!selected || !campaignId}
            />
          </Section>

          <Section title="Support level (optional)">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 8,
              }}
            >
              {SENTIMENT.map((s) => (
                <button
                  key={s.value}
                  onClick={() =>
                    !selected
                      ? remindSelectVoter()
                      : setSupport(s.value === support ? null : s.value)
                  }
                  style={{
                    ...CHIP_BASE,
                    ...(support === s.value ? CHIP_ACTIVE : {}),
                    ...(!selected || !campaignId ? CHIP_DISABLED : {}),
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: s.color,
                      marginRight: 8,
                      filter: support === s.value ? "brightness(1.3)" : "none",
                    }}
                  />
                  {s.label}
                </button>
              ))}
            </div>
          </Section>

          <Section title="Notes (optional)">
            <textarea
              value={cNotes}
              onChange={(e) => setCNotes(e.target.value)}
              rows={3}
              placeholder="Short note (e.g., dog at gate; prefers mail; call back evening)…"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                opacity: !selected || !campaignId ? 0.5 : 1,
              }}
              disabled={!selected || !campaignId}
            />
          </Section>
        </>
      )}

      {/* Note */}
      {mode === "note" && (
        <>
          <Section title="Note type (optional)">
            <input
              ref={firstControlRef as any}
              value={nType}
              onChange={(e) => setNType(e.target.value)}
              placeholder="e.g., Canvass, Phonebank"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                opacity: !selected || !campaignId ? 0.5 : 1,
              }}
              disabled={!selected || !campaignId}
            />
          </Section>
          <Section title="Note">
            <textarea
              value={nText}
              onChange={(e) => setNText(e.target.value)}
              rows={3}
              placeholder="Add a note about this interaction…"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                opacity: !selected || !campaignId ? 0.5 : 1,
              }}
              disabled={!selected || !campaignId}
            />
          </Section>
        </>
      )}

      {/* Relationship */}
      {mode === "relationship" && (
        <>
          <Section title="Relationship type">
            <select
              ref={firstControlRef as any}
              value={relTypeId}
              onChange={(e) =>
                !selected ? remindSelectVoter() : setRelTypeId(e.target.value)
              }
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                opacity: !selected || !campaignId ? 0.5 : 1,
              }}
              disabled={!selected || !campaignId}
            >
              <option value="">Select relationship…</option>
              {relTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </Section>

          <Section title="Relate to (search)">
            <input
              value={relSearch}
              onChange={(e) => setRelSearch(e.target.value)}
              placeholder="Search by name or address…"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                marginBottom: 8,
                opacity: !selected || !campaignId ? 0.5 : 1,
              }}
              disabled={!selected || !campaignId}
            />
            {relTarget ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  opacity: !selected || !campaignId ? 0.5 : 1,
                }}
              >
                <div className="badge">
                  {fullName(relTarget) || "(no name)"} — {relTarget.address1 ?? ""}{" "}
                  {relTarget.city ? `• ${relTarget.city}` : ""}{" "}
                  {relTarget.state ? `, ${relTarget.state}` : ""}{" "}
                  {relTarget.zip ? ` ${relTarget.zip}` : ""}
                </div>
                <button
                  style={{ ...BTN, ...(!selected || !campaignId ? CHIP_DISABLED : {}) }}
                  onClick={() => setRelTarget(null)}
                  disabled={!selected || !campaignId}
                >
                  Change
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 8,
                  opacity: !selected || !campaignId ? 0.5 : 1,
                }}
              >
                {relHits.map((h: VoterHit) => (
                  <button
                    key={h.campaign_voter_id + "-rel"}
                    style={{
                      ...BTN,
                      textAlign: "left",
                      ...(!selected || !campaignId ? CHIP_DISABLED : {}),
                    }}
                    onClick={() =>
                      !selected ? remindSelectVoter() : setRelTarget(h)
                    }
                    disabled={!selected || !campaignId}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {fullName(h) || "(no name)"}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: 13 }}>
                      {h.address1 ?? ""} {h.city ? `• ${h.city}` : ""}{" "}
                      {h.state ? `, ${h.state}` : ""} {h.zip ? ` ${h.zip}` : ""}
                    </div>
                  </button>
                ))}
                {!relSearch.trim() && (
                  <div style={{ color: "#6b7280" }}>Type to search…</div>
                )}
                {relSearch.trim() && relHits.length === 0 && (
                  <div style={{ color: "#6b7280" }}>No matches.</div>
                )}
              </div>
            )}
          </Section>

          <Section title="Options">
            <label className="check" style={{ opacity: !selected || !campaignId ? 0.5 : 1 }}>
              <input
                type="checkbox"
                checked={reciprocal}
                onChange={(e) => setReciprocal(e.target.checked)}
                disabled={!selected || !campaignId}
              />
              <span>Create reciprocal relationship</span>
            </label>
          </Section>

          <Section title="Note (optional)">
            <input
              value={relNote}
              onChange={(e) => setRelNote(e.target.value)}
              placeholder="Note about this relationship…"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                opacity: !selected || !campaignId ? 0.5 : 1,
              }}
              disabled={!selected || !campaignId}
            />
          </Section>
        </>
      )}

      {/* Save (sticky) */}
      <div style={{ position: "sticky", bottom: 0, paddingTop: 8 }}>
        <div
          style={{
            background: "rgba(243,247,251,0.9)",
            backdropFilter: "blur(4px)",
            padding: 8,
          }}
        >
          <button
            onClick={() => (!selected ? remindSelectVoter() : save())}
            disabled={
              !campaignId ||
              saving ||
              !selected ||
              (mode === "contact" && !canSaveContact) ||
              (mode === "note" && !canSaveNote) ||
              (mode === "relationship" && !canSaveRel)
            }
            style={{
              ...PRIMARY,
              width: "100%",
              padding: "14px 16px",
              fontSize: 16,
              opacity:
                !campaignId ||
                saving ||
                !selected ||
                (mode === "contact" && !canSaveContact) ||
                (mode === "note" && !canSaveNote) ||
                (mode === "relationship" && !canSaveRel)
                  ? 0.6
                  : 1,
            }}
          >
            {saving
              ? "Saving…"
              : mode === "contact"
              ? "Save Contact"
              : mode === "note"
              ? "Save Note"
              : "Save Relationship"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** ---------- Shared subcomponents ---------- */
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: PANEL.bg,
        borderRadius: PANEL.radius,
        border: PANEL.border,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

function Chip({
  label,
  active,
  disabled,
  onClick,
  buttonRef,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef as any}
      onClick={onClick}
      style={{
        ...CHIP_BASE,
        ...(active ? CHIP_ACTIVE : {}),
        ...(disabled ? CHIP_DISABLED : {}),
      }}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

function ChipRowDynamic({
  items,
  value,
  onChange,
  disabled,
  firstRef,
}: {
  items: Array<{ key: string; label: string }>;
  value: string | null;
  onChange: (v: string) => void;
  disabled?: boolean;
  firstRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: 8,
      }}
    >
      {items.map((it, idx) => {
        const active = it.key === value;
        return (
          <button
            key={it.key}
            ref={idx === 0 ? (firstRef as any) : undefined}
            onClick={() => onChange(active ? "" : it.key)}
            style={{
              ...CHIP_BASE,
              ...(active ? CHIP_ACTIVE : {}),
              ...(disabled ? CHIP_DISABLED : {}),
            }}
            title={it.key}
            disabled={!!disabled}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

